import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import Icons from "../icons";
import loginbg from "../../assets/loginbg.png";
import "../../pages/styles/ForgotPassword.css";

const ForgotPasswordForm = () => {
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isValid },
  } = useForm({ mode: "onChange" });

  const navigate = useNavigate();

  // ── Redux state and actions ────────────────
  const {
    sendOtp,
    isForgotLoading,
    forgotError,
    forgotPasswordSuccess,
    clearErrors,
    clearSuccess,
  } = useAuth();

  // ─────────────────────────────────────────
  // EFFECT: Navigate to OTP page when email is sent successfully
  //
  // KEY LEARNING — passing state between pages:
  // navigate('/otp', { state: { email } })
  // This passes the email to the next page via router state.
  // OtpPage reads it with: location.state?.email
  // This is the same pattern you already had — we're keeping it.
  // ─────────────────────────────────────────
  useEffect(() => {
    if (forgotPasswordSuccess) {
      const email = getValues("email");
      clearSuccess(); // reset the flag so it doesn't trigger again
      navigate("/otp", { state: { email } });
    }
  }, [forgotPasswordSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear errors when component unmounts (user navigates away)
  useEffect(() => {
    return () => clearErrors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = (data) => {
    // sendOtp dispatches forgotPassword(email) thunk
    // Loading, error, success are all handled by Redux
    sendOtp(data.email);
  };

  return (
    <div className="forgot-wrapper">
      {/* LEFT */}
      <div className="forgot-left">
        <img src={loginbg} alt="illustration" />
        <p>
          Unleash Your Team's Potential With QwickHub <br />
          Built For Modern Product Teams
        </p>
      </div>

      {/* RIGHT */}
      <div className="forgot-right">
        <div className="absolute top-4 right-6 flex items-center gap-0 cursor-pointer">
          <Icons.Logo />
          <span className="text-xl font-semibold">QwickHub</span>
        </div>

        <div className="forgot-card" >
          <h2 className="title text-3xl font-semibold p-5">Reset Your Password</h2>
          <p className="forgot-subtitle forgot-center">
Enter your registered email address and we’ll send a 6-digit verification code to reset your password.</p>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Backend error from Redux — replaces alert() */}
            {forgotError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{forgotError}</p>
              </div>
            )}

            <div>
              <label>Email</label>
              <input
                type="email"
                placeholder="john.doe@gmail.com"
                disabled={isForgotLoading}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Invalid email format",
                  },
                })}
              />
              {errors.email && (
                <span className="forgot-error">{errors.email.message}</span>
              )}
            </div>

            <button
              type="submit"
              disabled={isForgotLoading || !isValid}
              className="forgot-btn"
            >
              {isForgotLoading ? "Sending..." : "Send Code"}
            </button>
          </form>

          <p className="forgot-back">
            <a href="/">Back to Login</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
